/*
 * AiAdvisorFlagship.js — Phase 4 flagship AI Advisor tab.
 *
 * Consumes the /insights response's per-widget summaries (commerce, customers,
 * traffic, paid_media) plus the top-level opportunity/risk/recommendation and
 * synthesises a ranked "3 things to do this week" list.
 *
 * Every card is clickable → onNavigateToTab(target) deep-links back into the
 * source tab so the operator can immediately act on the insight.
 */
import React, { useMemo } from "react";
import { Sparkle, Target, Warning, TrendUp, ArrowRight, Trophy } from "@phosphor-icons/react";

// Which tab to deep-link into, per widget key
const WIDGET_TAB_MAP = {
  commerce: "commerce",
  customers: "commerce",
  traffic: "marketing",
  paid_media: "marketing",
  seo: "seo",
};

const WIDGET_LABEL = {
  commerce: "Commerce",
  customers: "Customers",
  traffic: "Marketing",
  paid_media: "Marketing",
  seo: "SEO",
};

// ── Flatten widget insights into ranked lanes ───────────────────────────────
const buildLanes = (intel) => {
  const wins = [];
  const risks = [];
  const opportunities = [];
  const recommendations = [];

  const widgets = intel?.widgets || {};
  Object.entries(widgets).forEach(([widget, val]) => {
    if (!val || typeof val !== "object") return;
    const tab = WIDGET_TAB_MAP[widget] || null;
    const source = WIDGET_LABEL[widget] || widget;
    ["opportunity", "risk", "recommendation"].forEach((kind) => {
      const item = val[kind];
      if (!item || typeof item !== "object") return;
      if (!item.title && !item.detail) return;
      const enriched = { ...item, source, widget, tab };
      if (kind === "opportunity") opportunities.push(enriched);
      else if (kind === "risk") risks.push(enriched);
      else recommendations.push(enriched);
    });
  });

  // Top-level insights get promoted to the front
  if (intel?.opportunity?.title) opportunities.unshift({ ...intel.opportunity, source: "Executive", tab: null });
  if (intel?.risk?.title) risks.unshift({ ...intel.risk, source: "Executive", tab: null });
  if (intel?.recommendation?.title) recommendations.unshift({ ...intel.recommendation, source: "Executive", tab: null });

  // "Wins" are derived from kpi_deltas with verdict === "positive"
  (intel?.kpi_deltas || []).forEach((d) => {
    if (d?.verdict === "positive" && d?.delta_pct != null) {
      wins.push({
        title: `${d.label || d.metric} up ${d.delta_pct?.toFixed?.(1) || d.delta_pct}%`,
        detail: `${d.label || d.metric} moved from ${d.previous} to ${d.current}. Keep the momentum.`,
        source: d.source ? d.source[0].toUpperCase() + d.source.slice(1) : "Executive",
        tab: d.source === "ga4" || d.source === "meta" ? "marketing" : "commerce",
      });
    }
  });

  return { wins, risks, opportunities, recommendations };
};

// ── The "if you only do 3 things this week" plan ────────────────────────────
const buildWeeklyPlan = (recommendations, opportunities) => {
  const combined = [...recommendations, ...opportunities];
  // Simple dedupe on title
  const seen = new Set();
  const plan = [];
  for (const c of combined) {
    const key = (c?.title || "").toLowerCase().slice(0, 60);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    plan.push(c);
    if (plan.length >= 3) break;
  }
  return plan;
};

// ── Lane card ───────────────────────────────────────────────────────────────
const LaneCard = ({ title, tone, items, Icon, onNavigate, testid }) => {
  const palette = {
    win: { chip: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", dot: "bg-emerald-400", ring: "border-emerald-500/20", iconColor: "text-emerald-300" },
    risk: { chip: "bg-red-500/15 text-red-300 border-red-500/30", dot: "bg-red-400", ring: "border-red-500/20", iconColor: "text-red-300" },
    opportunity: { chip: "bg-sky-500/15 text-sky-300 border-sky-500/30", dot: "bg-sky-400", ring: "border-sky-500/20", iconColor: "text-sky-300" },
    action: { chip: "bg-[#FFEB00]/15 text-[#FFEB00] border-[#FFEB00]/40", dot: "bg-[#FFEB00]", ring: "border-[#FFEB00]/30", iconColor: "text-[#FFEB00]" },
  }[tone];

  return (
    <div className={`p-4 rounded-xl bg-[#0B0B0F] border ${palette.ring}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={14} weight="fill" className={palette.iconColor} />
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-white/70">{title}</h4>
        </div>
        <span className={`text-[9px] px-1.5 py-0.5 rounded border ${palette.chip}`}>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-white/40 italic">Nothing surfaced for this window.</p>
      ) : (
        <ul className="space-y-3">
          {items.slice(0, 5).map((it, idx) => (
            <li key={idx}>
              <button
                type="button"
                onClick={() => it.tab && onNavigate?.(it.tab)}
                disabled={!it.tab}
                className={`text-left w-full group ${it.tab ? "cursor-pointer" : "cursor-default"}`}
                data-testid={`${testid}-item-${idx}`}
              >
                <div className="flex items-start gap-2.5">
                  <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${palette.dot}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] text-white/95 font-medium leading-snug group-hover:text-white transition-colors">{it.title}</p>
                    {it.detail && <p className="text-[11px] text-white/60 mt-0.5 leading-relaxed line-clamp-2">{it.detail}</p>}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {it.source && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.05] border border-white/10 text-white/60 uppercase tracking-wider">
                          {it.source}
                        </span>
                      )}
                      {(it.impact || it.revenue_impact || it.estimated_impact) && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.05] border border-white/10 text-white/70">
                          {it.impact || it.revenue_impact || it.estimated_impact}
                        </span>
                      )}
                      {it.priority && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.05] border border-white/10 text-white/70">
                          Priority: {it.priority}
                        </span>
                      )}
                      {it.tab && (
                        <span className="text-[9px] text-white/40 uppercase tracking-wider group-hover:text-[#FFEB00] transition-colors flex items-center gap-0.5">
                          View source <ArrowRight size={9} weight="bold" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── Weekly plan card (flagship) ─────────────────────────────────────────────
const WeeklyPlan = ({ plan, onNavigate }) => {
  if (!plan?.length) return null;
  return (
    <div className="p-5 rounded-xl border border-[#FFEB00]/40 bg-gradient-to-br from-[#FFEB00]/10 via-transparent to-transparent" data-testid="advisor-weekly-plan">
      <div className="flex items-center gap-2 mb-3">
        <Sparkle size={16} weight="fill" className="text-[#FFEB00]" />
        <h3 className="text-sm font-semibold text-white uppercase tracking-wider" style={{ fontFamily: "Varela Round, sans-serif" }}>If you only do 3 things this week</h3>
      </div>
      <ol className="space-y-2.5">
        {plan.map((p, idx) => (
          <li key={idx}>
            <button
              type="button"
              onClick={() => p.tab && onNavigate?.(p.tab)}
              disabled={!p.tab}
              className={`text-left w-full flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/10 ${p.tab ? "hover:bg-white/[0.05] hover:border-white/20 cursor-pointer" : "cursor-default"} transition`}
              data-testid={`advisor-plan-item-${idx}`}
            >
              <span className="text-lg font-bold text-[#FFEB00] leading-none w-6 shrink-0">{idx + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-white leading-snug">{p.title}</p>
                {p.detail && <p className="text-[11px] text-white/70 mt-1 leading-relaxed line-clamp-2">{p.detail}</p>}
                {p.source && (
                  <span className="inline-block mt-2 text-[9px] px-1.5 py-0.5 rounded bg-white/[0.05] border border-white/10 text-white/60 uppercase tracking-wider">
                    {p.source}
                  </span>
                )}
              </div>
              {p.tab && <ArrowRight size={12} weight="bold" className="text-white/40 mt-1 shrink-0" />}
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
};

// ── Main component ──────────────────────────────────────────────────────────
const AiAdvisorFlagship = ({ intel, loading, onNavigateToTab }) => {
  const lanes = useMemo(() => buildLanes(intel), [intel]);
  const weeklyPlan = useMemo(() => buildWeeklyPlan(lanes.recommendations, lanes.opportunities), [lanes]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="advisor-loading">
        <div className="p-5 rounded-xl bg-white/[0.02] border border-white/10 animate-pulse">
          <div className="h-3 bg-white/10 rounded w-40 mb-3" />
          <div className="h-4 bg-white/10 rounded w-full mb-2" />
          <div className="h-4 bg-white/10 rounded w-11/12" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[1,2,3,4].map(i => (
            <div key={i} className="p-4 rounded-xl bg-white/[0.02] border border-white/10 animate-pulse h-32" />
          ))}
        </div>
      </div>
    );
  }

  const summary = intel?.executive_summary;
  const nothingSurfaced = !summary && !weeklyPlan.length &&
    !lanes.wins.length && !lanes.risks.length &&
    !lanes.opportunities.length && !lanes.recommendations.length;

  if (nothingSurfaced) {
    return (
      <div className="p-8 rounded-xl bg-white/[0.02] border border-white/10 text-center space-y-3" data-testid="advisor-empty">
        <Sparkle size={28} weight="light" className="mx-auto text-white/40" />
        <p className="text-sm text-white/70 max-w-lg mx-auto leading-relaxed">
          AI Advisor is still warming up. Once Shopify, Meta, GA4 and Search Console have data flowing
          for this window, ranked wins, risks, opportunities and recommendations will appear here — each
          clickable straight to its source report.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="advisor-flagship">
      {/* Executive summary */}
      {summary && (
        <div className="p-5 rounded-xl bg-gradient-to-br from-[#FFEB00]/8 via-transparent to-transparent border border-[#FFEB00]/20" data-testid="advisor-exec-summary">
          <div className="flex items-start gap-3">
            <Sparkle size={18} weight="fill" className="text-[#FFEB00] mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#FFEB00]/80 font-semibold mb-1.5">Executive Summary</p>
              <p className="text-sm text-white/90 leading-relaxed">{summary}</p>
              {intel?.confidence && (
                <p className="text-[10px] text-white/50 mt-2 uppercase tracking-wider">Confidence: {intel.confidence}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Weekly plan — the flagship */}
      <WeeklyPlan plan={weeklyPlan} onNavigate={onNavigateToTab} />

      {/* Ranked lanes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <LaneCard title="Top Wins"           tone="win"         Icon={Trophy}  items={lanes.wins}            onNavigate={onNavigateToTab} testid="advisor-wins" />
        <LaneCard title="Top Risks"          tone="risk"        Icon={Warning} items={lanes.risks}           onNavigate={onNavigateToTab} testid="advisor-risks" />
        <LaneCard title="Top Opportunities"  tone="opportunity" Icon={Target}  items={lanes.opportunities}   onNavigate={onNavigateToTab} testid="advisor-opportunities" />
        <LaneCard title="Recommendations"    tone="action"      Icon={TrendUp} items={lanes.recommendations} onNavigate={onNavigateToTab} testid="advisor-recommendations" />
      </div>
    </div>
  );
};

export default AiAdvisorFlagship;
